import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import * as S from './styles';
import BigNumber from 'bignumber.js';
import type { CoinTier } from '../types';
import { useTranslation } from 'react-i18next';

interface LeverageTableProps {
  coinTiers: CoinTier[];
}

export function LeverageTable({ coinTiers }: LeverageTableProps) {
  const { t } = useTranslation();

  const columns: ColumnsType<CoinTier> = [
    {
      title: t('tier'),
      dataIndex: 'level',
      key: 'level',
      width: 118,
      render: (tier) => <S.Cell $color="#b3b5bd">{tier}</S.Cell>,
    },
    {
      title: t('PositionBracket'),
      dataIndex: 'positionBracket',
      key: 'positionBracket',
      width: 360,
      align: 'left',
      render: (value, data) => (
        <S.Cell>{`${BigNumber(data?.notional_min).toFormat()} ~ ${BigNumber(data?.notional_max).toFormat()}`}</S.Cell>
      ),
    },
    {
      title: t('MaxLeverage'),
      dataIndex: 'max_leverage',
      key: 'max_leverage',
      width: 264,
      align: 'left',
      render: (text) => <S.Cell>{text + ' x'}</S.Cell>,
    },
    {
      title: t('MaintenanceMarginRate'),
      dataIndex: 'mmr',
      key: 'mmr',
      width: 304,
      align: 'left',
      render: (text) => (
        <S.Cell>{BigNumber(text).multipliedBy(100).toFormat(2) + ' %'}</S.Cell>
      ),
    },
    {
      title: t('MaintenanceAmount'),
      dataIndex: 'ma',
      key: 'ma',
      align: 'left',
      render: (text) => <S.Cell>{BigNumber(text).toFormat()}</S.Cell>,
    },
  ];

  // 데이터에 key 추가
  const dataWithKeys = coinTiers.map((item) => ({
    ...item,
    key: item.level,
  }));

  return (
    <S.Wrapper>
      {/* 데스크탑: 테이블 뷰 */}
      <S.DesktopTableWrapper>
        <S.StyledTable
          columns={columns}
          dataSource={dataWithKeys}
          pagination={false}
          rowKey="level"
          scroll={{ x: 'max-content' }}
        />
      </S.DesktopTableWrapper>

      {/* 모바일: 카드 뷰 */}
      <S.MobileCardList>
        {coinTiers.map((tier) => (
          <S.Card key={tier.level}>
            <S.CardHeader>
              <S.CardTitle>{`${t('tier')} ${tier.level}`}</S.CardTitle>
            </S.CardHeader>
            <S.CardDivider />
            <S.CardContent>
              {/* Position Bracket */}
              <S.CardRow>
                <S.CardItem>
                  <S.CardLabel>{t('PositionBracket')}</S.CardLabel>
                  <S.CardValue>
                    {`${BigNumber(tier.notional_min).toFormat()} ~ ${BigNumber(tier.notional_max).toFormat()}`}
                  </S.CardValue>
                </S.CardItem>
              </S.CardRow>

              {/* Max Leverage & Maintenance Margin Rate */}
              <S.CardRowDouble>
                <S.CardItem>
                  <S.CardLabel>{t('MaxLeverage')}</S.CardLabel>
                  <S.CardValue>{tier.max_leverage}x</S.CardValue>
                </S.CardItem>
                <S.CardItem>
                  <S.CardLabel>{t('MaintenanceMarginRate')}</S.CardLabel>
                  <S.CardValue>
                    {BigNumber(tier.mmr).multipliedBy(100).toFormat(2)}%
                  </S.CardValue>
                </S.CardItem>
              </S.CardRowDouble>

              {/* Maintenance Amount */}
              <S.CardRow>
                <S.CardItem>
                  <S.CardLabel>{t('MaintenanceAmount')}</S.CardLabel>
                  <S.CardValue>{BigNumber(tier.ma).toFormat()}</S.CardValue>
                </S.CardItem>
              </S.CardRow>
            </S.CardContent>
          </S.Card>
        ))}
      </S.MobileCardList>
    </S.Wrapper>
  );
}
